import { useMemo, useState } from "react";
import { Search, CalendarClock, History as HistoryIcon, CalendarDays, CalendarCheck } from "lucide-react";
import Bookings from "./Bookings.jsx";
import History from "./History.jsx";
import Avalibility from "./Avalibility.jsx";
import { UPCOMING_BOOKINGS, ORDER_HISTORY } from "./bookingUtils.jsx";

const TABS = [
    { key: "upcoming", label: "Requests", icon: CalendarClock },
    { key: "availability", label: "Availability", icon: CalendarDays },
    { key: "upcomingSessions", label: "Upcoming", icon: CalendarCheck },
    { key: "history", label: "Order History", icon: HistoryIcon },
];

export default function OrderManagement() {
    const [tab, setTab] = useState("upcoming"); // "upcoming" | "availability" | "upcomingSessions" | "history"
    const [search, setSearch] = useState("");
    const [historyFilter, setHistoryFilter] = useState("All");

    const [upcoming, setUpcoming] = useState(UPCOMING_BOOKINGS);
    const [history, setHistory] = useState(ORDER_HISTORY);

    const filteredUpcoming = useMemo(
        () =>
            upcoming.filter(
                (b) =>
                    b.mentorName.toLowerCase().includes(search.toLowerCase()) ||
                    b.course.toLowerCase().includes(search.toLowerCase())
            ),
        [upcoming, search]
    );

    const filteredHistory = useMemo(
        () =>
            history.filter((h) => {
                const matchesSearch =
                    h.mentorName.toLowerCase().includes(search.toLowerCase()) ||
                    h.course.toLowerCase().includes(search.toLowerCase());
                const matchesStatus = historyFilter === "All" || h.status === historyFilter.toLowerCase();
                return matchesSearch && matchesStatus;
            }),
        [history, search, historyFilter]
    );

    // Sessions that have moved past the request stage and are confirmed on the calendar.
    const filteredUpcomingSessions = useMemo(
        () =>
            filteredUpcoming.filter((b) => b.status ? b.status === "confirmed" : true),
        [filteredUpcoming]
    );

    const cancelBooking = (id) => {
        const booking = upcoming.find((b) => b.id === id);
        if (!booking) return;
        setUpcoming((prev) => prev.filter((b) => b.id !== id));
        setHistory((prev) => [{ ...booking, status: "cancelled", myRating: null }, ...prev]);
    };

    const tabCounts = {
        upcoming: upcoming.length,
        availability: null,
        upcomingSessions: filteredUpcomingSessions.length,
        history: history.length,
    };

    return (
        <div className="min-h-screen bg-slate-50 p-3 sm:p-5">
            <div className="mx-auto ">
                {/* Tabs + search */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-2">
                        {TABS.map(({ key, label, icon: Icon }) => {
                            const count = tabCounts[key];
                            const active = tab === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setTab(key)}
                                    className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${active
                                        ? "border-indigo-600 bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm"
                                        : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
                                        }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {label}
                                    {count !== null && (
                                        <span className={`rounded-full px-1.5 text-xs ${active ? "bg-white/20" : "bg-slate-100"}`}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex gap-2">
                        {tab !== "availability" && (
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search mentor or course..."
                                    className="w-56 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                        )}
                        {tab === "history" && (
                            <select
                                value={historyFilter}
                                onChange={(e) => setHistoryFilter(e.target.value)}
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                                <option value="All">All statuses</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        )}
                    </div>
                </div>

                {tab === "upcoming" && <Bookings bookings={filteredUpcoming} onCancel={cancelBooking} />}

                {tab === "availability" && <Avalibility bookings={upcoming} />}

                {tab === "upcomingSessions" && (
                    <Bookings
                        bookings={filteredUpcomingSessions}
                        onCancel={cancelBooking}
                        showJoinMeeting
                    />
                )}

                {tab === "history" && <History history={filteredHistory} />}
            </div>
        </div>
    );
}